import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import StudioV2AppRuntimeShell from '../StudioV2AppRuntimeShell';
import { virtualMatchMedia } from './virtualMatchMedia';

const DEVICE_WIDTH = { desktop: 1200, tablet: 834, phone: 390 };

/**
 * The real app runtime rendered at device width and scaled to fit its column,
 * so the builder shows exactly what visitors will see.
 */
export default function AppLivePreview({
  definition,
  mongoId,
  height = 620,
  live = false,
  onLiveChange,
  showControls = true,
  device: deviceProp,
}) {
  const frameRef = useRef(null);
  const [frameWidth, setFrameWidth] = useState(0);
  const [deviceState, setDeviceState] = useState('desktop');
  const device = deviceProp || deviceState;

  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setFrameWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const innerWidth = DEVICE_WIDTH[device];
  const scale = frameWidth ? Math.min(1, frameWidth / innerWidth) : 1;
  const innerHeight = Math.round(height / scale);
  const matchMedia = useMemo(() => virtualMatchMedia(innerWidth), [innerWidth]);
  const deviceTheme = useCallback((outer) => ({
    ...outer,
    components: {
      ...outer.components,
      MuiUseMediaQuery: { defaultProps: { matchMedia, noSsr: true } },
    },
  }), [matchMedia]);

  return (
    <Stack spacing={1.25} sx={{ height: '100%' }}>
      {showControls && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={device}
            onChange={(_, v) => v && setDeviceState(v)}
            aria-label="Preview device"
          >
            <ToggleButton value="desktop" aria-label="Desktop"><Tooltip title="Desktop"><DesktopWindowsOutlinedIcon fontSize="small" /></Tooltip></ToggleButton>
            <ToggleButton value="tablet" aria-label="Tablet"><Tooltip title="Tablet"><TabletMacOutlinedIcon fontSize="small" /></Tooltip></ToggleButton>
            <ToggleButton value="phone" aria-label="Phone"><Tooltip title="Phone"><PhoneIphoneOutlinedIcon fontSize="small" /></Tooltip></ToggleButton>
          </ToggleButtonGroup>
          {onLiveChange && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={live ? 'live' : 'sample'}
              onChange={(_, v) => v && onLiveChange(v === 'live')}
              aria-label="Preview data"
            >
              <ToggleButton value="sample" sx={{ textTransform: 'none', px: 1.5 }}>Sample data</ToggleButton>
              <ToggleButton value="live" sx={{ textTransform: 'none', px: 1.5 }}>Try it live</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
      )}
      <Box
        ref={frameRef}
        data-testid="app-live-preview"
        sx={{
          position: 'relative',
          height,
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'grey.100',
          border: 1,
          borderColor: 'divider',
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {definition ? (
          <Box
            sx={{
              width: innerWidth,
              height: innerHeight,
              flex: '0 0 auto',
              transform: `scale(${scale})`,
              transformOrigin: 'top center',
              bgcolor: 'background.default',
            }}
          >
            <ThemeProvider theme={deviceTheme}>
              <StudioV2AppRuntimeShell
                key={device}
                definition={definition}
                mongoId={mongoId}
                studioPreview={!live}
                height={innerHeight}
              />
            </ThemeProvider>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', px: 3, textAlign: 'center' }}>
            The preview appears once the app settings can be read.
          </Typography>
        )}
      </Box>
      {live && (
        <Typography variant="caption" color="text.secondary">
          Live mode runs your saved flows. Save your changes first so the flows see them.
        </Typography>
      )}
    </Stack>
  );
}
